import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'hooks';
import { register } from 'store/slices/authSlice';
import AuthLayout from './AuthLayout';
import ResendConfirmation from './ResendConfirmation';

const RegisterScreen: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const dispatch = useAppDispatch();
  const auth = useAppSelector(state => state.auth);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Create your EasyChatDesk account';
    return () => {
      document.title = prev;
    };
  }, []);

  if (auth.token) {
    return <Navigate to="/" />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (name.trim().length < 2) return setError('Please enter your name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError('Please enter a valid email.');
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (password !== confirm) return setError('Passwords do not match.');

    setLoading(true);
    const result = await dispatch(register({ name, email, password }));
    setLoading(false);

    if (register.fulfilled.match(result)) {
      setDone(true);
    } else {
      setError((result.payload as string) || 'Registration failed.');
    }
  };

  if (done) {
    return (
      <AuthLayout title="Check your inbox" subtitle="One more step to activate your account">
        <div className="ecd-auth__alert ecd-auth__alert--success">
          We&apos;ve sent a confirmation link to <strong>{email}</strong>. Click it to activate
          your account, then log in.
        </div>
        <div className="ecd-auth__hint">
          <ResendConfirmation email={email} />
        </div>
        <div className="ecd-auth__hint" style={{ marginTop: 6 }}>
          <Link to="/login" className="ecd-auth__link">
            Back to login
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Start chatting with your customers in minutes"
    >
      {error && <div className="ecd-auth__alert ecd-auth__alert--danger">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="ecd-auth__field">
          <label className="ecd-auth__label" htmlFor="name">
            Name
          </label>
          <input
            id="name"
            type="text"
            className="ecd-auth__input"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Jane Founder"
            autoComplete="name"
          />
        </div>
        <div className="ecd-auth__field">
          <label className="ecd-auth__label" htmlFor="email">
            Email address
          </label>
          <input
            id="email"
            type="email"
            className="ecd-auth__input"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
          />
        </div>
        <div className="ecd-auth__field">
          <label className="ecd-auth__label" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            type="password"
            className="ecd-auth__input"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            autoComplete="new-password"
          />
        </div>
        <div className="ecd-auth__field">
          <label className="ecd-auth__label" htmlFor="confirm">
            Confirm password
          </label>
          <input
            id="confirm"
            type="password"
            className="ecd-auth__input"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            placeholder="Re-enter your password"
            autoComplete="new-password"
          />
        </div>

        <button type="submit" className="ecd-auth__btn" disabled={loading}>
          {loading ? 'Creating account…' : 'Create account'}
        </button>
      </form>

      <div className="ecd-auth__hint">
        Already have an account?{' '}
        <Link to="/login" className="ecd-auth__link">
          Log in
        </Link>
      </div>
    </AuthLayout>
  );
};

export default RegisterScreen;
